/**
 * `<plp-tabs>` + `<plp-tab>` — табы.
 *
 * `<plp-tabs>` рендерит navigation-bar из своих `<plp-tab title="...">`.
 * Контент `<plp-tab>` показывается только когда таб активен.
 *
 * ARIA: role="tablist" / "tab" / "tabpanel", aria-selected/aria-controls.
 */

const VARIANT_CLASSES: Record<string, { btn: string; active: string }> = {
  pills: {
    btn: 'plp-tab-btn px-4 py-2 rounded-full text-sm font-medium transition-colors',
    active: 'bg-rose-600 text-white',
  },
  underline: {
    btn:
      'plp-tab-btn px-1 pb-2 text-sm font-medium border-b-2 border-transparent ' +
      'transition-colors',
    active: 'border-rose-600 text-rose-600',
  },
  bordered: {
    btn:
      'plp-tab-btn px-4 py-2 text-sm font-medium border border-gray-200 rounded-t ' +
      '-mb-px transition-colors',
    active: 'bg-white border-b-white text-rose-600',
  },
};

export class PlpTab extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['title', 'id', 'default'];
  }

  private _slug?: string;

  connectedCallback(): void {
    this.ensureId();
    this.style.display = '';
    // tabs parent перерисует navigation; тут только устанавливаем role
    this.setAttribute('role', 'tabpanel');
  }

  attributeChangedCallback(): void {
    if (this.isConnected) {
      this.ensureId();
      const tabs = this.closest('plp-tabs');
      if (tabs instanceof PlpTabs) tabs.refresh();
    }
  }

  ensureId(): string {
    if (this.id) return this.id;
    const fromAttr = this.getAttribute('id');
    if (fromAttr) {
      this.id = fromAttr;
      return this.id;
    }
    const title = this.getAttribute('title') ?? '';
    const slug =
      title
        .toLowerCase()
        .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
        .replace(/^-+|-+$/g, '') || `tab-${Math.random().toString(36).slice(2, 8)}`;
    this._slug = slug;
    this.id = slug;
    return this.id;
  }

  setActive(active: boolean): void {
    if (active) {
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
    } else {
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
    }
  }
}

export class PlpTabs extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['default', 'variant'];
  }

  private _nav?: HTMLElement;
  private _activeId = '';

  connectedCallback(): void {
    this.setAttribute('role', 'tablist');
    queueMicrotask(() => this.refresh());
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.refresh();
  }

  private tabs(): PlpTab[] {
    return Array.from(this.querySelectorAll<PlpTab>(':scope > plp-tab'));
  }

  private variant(): { btn: string; active: string } {
    const v = (this.getAttribute('variant') ?? 'underline').toLowerCase();
    return VARIANT_CLASSES[v] ?? VARIANT_CLASSES.underline;
  }

  private pickInitialActive(): string {
    const tabs = this.tabs();
    if (tabs.length === 0) return '';
    const explicit = this.getAttribute('default');
    if (explicit && tabs.some((t) => t.ensureId() === explicit)) return explicit;
    const defaulted = tabs.find((t) => t.hasAttribute('default'));
    if (defaulted) return defaulted.ensureId();
    return tabs[0].ensureId();
  }

  refresh(): void {
    const tabs = this.tabs();
    if (tabs.length === 0) return;
    if (!this._activeId || !tabs.some((t) => t.id === this._activeId)) {
      this._activeId = this.pickInitialActive();
    }
    this.renderNav(tabs);
    for (const t of tabs) t.setActive(t.id === this._activeId);
  }

  private renderNav(tabs: PlpTab[]): void {
    if (!this._nav) {
      this._nav = document.createElement('div');
      this._nav.className = 'plp-tabs-nav flex gap-2 mb-4 border-b border-gray-200';
      this.insertBefore(this._nav, this.firstChild);
    }
    const { btn, active } = this.variant();
    this._nav.innerHTML = '';
    for (const tab of tabs) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = btn + (tab.id === this._activeId ? ' ' + active : '');
      button.textContent = tab.getAttribute('title') ?? tab.id;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', tab.id);
      button.setAttribute('aria-selected', String(tab.id === this._activeId));
      button.addEventListener('click', () => this.activate(tab.id));
      button.addEventListener('keydown', (e) => this.onNavKey(e, tab.id));
      this._nav.appendChild(button);
    }
  }

  private onNavKey(e: KeyboardEvent, id: string): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.activate(id);
      return;
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const tabs = this.tabs();
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return;
    const next =
      e.key === 'ArrowRight' ? (idx + 1) % tabs.length : (idx - 1 + tabs.length) % tabs.length;
    this.activate(tabs[next].id);
  }

  activate(id: string): void {
    this._activeId = id;
    this.refresh();
  }
}
